/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Greeting helpers for the v2 sidebar.
 *
 * Time of day comes from the browser clock, so it needs no permission.
 * Weather is opt-in: only after the user clicks "Show local weather" and the
 * browser grants location. It comes from Open-Meteo (free, no key), is cached
 * for 30 minutes in this browser, and the coordinates are never sent to our
 * server.
 */

import { useCallback, useEffect, useState } from "react";

const OPT_IN_KEY = "pearl.weather.optIn";
const CACHE_KEY = "pearl.weather.cache";
const CACHE_MS = 30 * 60 * 1000;

/** The first part of the login name, capitalised: "samrat.majumdar@x" is "Samrat". */
export function displayName(user) {
  const raw = (user?.username || "").split("@")[0].split(".")[0];
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "";
}

/** 5:00-11:59 morning, 12:00-16:59 afternoon, 17:00-19:59 evening, else night. */
export function partOfDay(date) {
  const h = date.getHours();
  if (h >= 5 && h < 12) return "Morning";
  if (h >= 12 && h < 17) return "Afternoon";
  if (h >= 17 && h < 20) return "Evening";
  return "Night";
}

/** WMO weather code to a label and the badge kind that replaces the clock. */
export function describeWeather(code, windKmh) {
  let label = "Clear";
  let kind = null;
  if ([1, 2].includes(code)) label = "Partly cloudy";
  else if (code === 3) label = "Cloudy";
  else if ([45, 48].includes(code)) label = "Fog";
  else if (code >= 51 && code <= 67) {
    label = "Rain";
    kind = "rain";
  } else if (code >= 71 && code <= 77) {
    label = "Snow";
    kind = "snow";
  } else if (code >= 80 && code <= 82) {
    label = "Showers";
    kind = "rain";
  } else if (code >= 85 && code <= 86) {
    label = "Snow showers";
    kind = "snow";
  } else if (code >= 95) {
    label = "Storm";
    kind = "rain";
  } else if (code === 0) label = "Sunny";
  if (!kind && windKmh >= 40) {
    label = `${label}, windy`;
    kind = "wind";
  }
  return { label, kind };
}

function read(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage blocked: weather just will not be remembered.
  }
}

export function useWeather() {
  const [enabled, setEnabled] = useState(() => read(OPT_IN_KEY) === "1");
  const [weather, setWeather] = useState(() => {
    try {
      const cached = JSON.parse(read(CACHE_KEY) || "null");
      return cached && Date.now() - cached.at < CACHE_MS ? cached.weather : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!enabled || weather || !navigator.geolocation) return undefined;
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const url =
            "https://api.open-meteo.com/v1/forecast" +
            `?latitude=${coords.latitude.toFixed(2)}&longitude=${coords.longitude.toFixed(2)}` +
            "&current=temperature_2m,weather_code,wind_speed_10m";
          const res = await fetch(url);
          const body = await res.json();
          const current = body?.current;
          if (cancelled || !current) return;
          const next = {
            temperature: current.temperature_2m,
            ...describeWeather(current.weather_code, current.wind_speed_10m),
          };
          setWeather(next);
          write(CACHE_KEY, JSON.stringify({ at: Date.now(), weather: next }));
        } catch {
          // Offline or blocked: keep the time-of-day badge.
        }
      },
      () => {
        // Location refused: turn the opt-in back off so the link shows again.
        write(OPT_IN_KEY, "0");
        if (!cancelled) setEnabled(false);
      },
      { maximumAge: CACHE_MS, timeout: 10000 }
    );
    return () => {
      cancelled = true;
    };
  }, [enabled, weather]);

  const enable = useCallback(() => {
    write(OPT_IN_KEY, "1");
    setEnabled(true);
  }, []);

  return { weather, enabled, enable };
}
